import { throttle } from "lodash"
import { findElementByXPath } from "@/utils"
import dayjs from "dayjs"

class UserTrackingService {
  constructor() {
    this.storageKey = "userTrackingData"
    this.metaKey = "userTrackingMeta"
    this.batchSize = 5000 // 每批最多存储5000条
    this.maxHours = 24 // 数据保留时间
    // 新增区域配置（可根据实际需求修改）
    this.areaSelectors = [
      { name: "微信号列表", selector: "#area-tag--1" },
      { name: "好友群聊通讯录等列表", selector: "#area-tag-2" },
      { name: "好友会话列表", selector: "#pane-msgFriend" },
      { name: "群聊列表", selector: "#pane-msgGroup" },
      { name: "通讯录列表", selector: "#pane-contacts" },
      { name: "筛选标签会话列表", selector: "#pane-moreLabel" },
      { name: "营销活动会话列表", selector: "#pane-activity" },
      { name: "添加好友列表", selector: "#pane-add" },
      { name: "线索列表", selector: "#pane-act" },
      { name: "聊天对话框区域", selector: "#area-tag-4" },
      { name: "侧边栏-素材中心", selector: "#pane-wctMaterial" },
      { name: "侧边栏-客户资料", selector: "#pane-memberInfo" },
      { name: "好友朋友圈", selector: ".wxChat--page" },
      { name: "侧边栏-商品推荐谭飞", selector: "#pane-mall-tf" },
      { name: "侧边栏-商品推荐欧莎", selector: "#pane-mall-osa" },
      { name: "侧边栏-订单明细", selector: "#pane-orders" },
      { name: "侧边栏-商品资料", selector: "#pane-products" },
      { name: "侧边栏-客户线索", selector: "#pane-customerLeads" },
      { name: "侧边栏-消费分析", selector: "#pane-consumptionAnalysis" },
      { name: "侧边栏-广告数据", selector: "#pane-pane-adData" }
    ]

    this.initMetadata()

    // 节流发送（5秒最多发送一次）
    this.sendBatches = throttle(this._sendBatches.bind(this), 5000)
  }

  initMetadata() {
    if (!localStorage.getItem(this.metaKey)) {
      localStorage.setItem(
        this.metaKey,
        JSON.stringify({
          currentBatch: 1,
          batches: {
            1: {
              created: new Date().toISOString(), // 创建时间
              lastUpdated: new Date().toISOString(),
              count: 0
            }
          }
        })
      )
    }
  }

  get metadata() {
    return JSON.parse(localStorage.getItem(this.metaKey))
  }

  updateMetadata(meta) {
    localStorage.setItem(this.metaKey, JSON.stringify(meta))
  }

  trackEvent(event) {
    this.checkStorage() // 新增检查
    // 立即存储到本地存储
    const trackInfo = this.createTrackInfo(event)
    this.saveEvent(trackInfo)
    this.sendBatches() // 触发发送检查
  }

  createTrackInfo(event) {
    const fullUrl = window.location.href
    const pathWithParams = fullUrl.replace(window.location.origin, "") // 直接替换掉域名部分
    return {
      timestamp: new Date().toISOString(),
      url: pathWithParams || "/", // 处理根路径情况
      elementType: event.target.tagName,
      elementId: event.target.id,
      elementClass: event.target.className,
      elementText: event.target.innerText,
      // 新增点击区域参数
      clickArea: this.getClickArea(event.target)
      // xPath: this.getXPath(event.target),
      // userAgent: navigator.userAgent
    }
  }

  saveEvent(eventData) {
    const meta = this.metadata
    const currentBatch = meta.currentBatch
    const batchKey = `${this.storageKey}_${currentBatch}`

    // 添加批次存在性检查
    if (!meta.batches[currentBatch]) {
      meta.batches[currentBatch] = {
        timestamp: new Date().toISOString(),
        count: 0
      }
    }

    // 获取并更新批次数据
    let batchData = JSON.parse(localStorage.getItem(batchKey) || "[]")
    batchData.push(eventData)

    // 更新批次元数据
    meta.batches[currentBatch].count = batchData.length
    meta.batches[currentBatch].timestamp = new Date().toISOString() // 更新最后修改时间
    meta.batches[currentBatch].lastUpdated = new Date().toISOString()

    if (batchData.length >= this.batchSize) {
      this.createNewBatch(meta)
    }

    // 写入存储
    localStorage.setItem(batchKey, JSON.stringify(batchData))
    this.updateMetadata(meta)
  }

  createNewBatch(meta) {
    const newBatch = meta.currentBatch + 1
    meta.currentBatch = newBatch
    meta.batches[newBatch] = {
      created: new Date().toISOString(), // 新增
      lastUpdated: new Date().toISOString(),
      count: 0
    }
    this.updateMetadata(meta)
  }

  async _sendBatches() {
    const meta = this.metadata
    const now = new Date()

    Object.entries(meta.batches).forEach(async ([batchNumber, batchMeta]) => {
      const batchKey = `${this.storageKey}_${batchNumber}`

      // 检查过期批次
      if (this.isBatchExpired(batchMeta.timestamp, now)) {
        console.log(`批次 ${batchNumber} 已过期，删除...`)
        localStorage.removeItem(batchKey)
        delete meta.batches[batchNumber]
        this.updateMetadata(meta)
        return
      }

      // 获取有效数据
      const batchData = JSON.parse(localStorage.getItem(batchKey) || "[]")
      const validData = batchData.filter(event => !this.isEventExpired(event.timestamp, now))

      // 发送数据
      if (validData.length > 0) {
        try {
          this.updateMetadata(meta)
        } catch (error) {}
      }
    })
  }

  isBatchExpired(createdTimestamp) {
    const now = dayjs()
    const createdTime = dayjs(createdTimestamp)
    return now.diff(createdTime, "hour") > this.maxHours
  }

  isEventExpired(eventTimestamp, now = new Date()) {
    const eventTime = new Date(eventTimestamp)
    return now - eventTime > this.maxHours * 60 * 60 * 1000
  }

  getXPath(element) {
    if (!element) return ""
    if (element.id) return `//*[@id="${element.id}"]`

    const paths = []
    while (element.nodeType === Node.ELEMENT_NODE) {
      let index = 0
      let sibling = element.previousSibling
      while (sibling) {
        if (sibling.nodeType === Node.ELEMENT_NODE && sibling.tagName === element.tagName) {
          index++
        }
        sibling = sibling.previousSibling
      }

      const tagName = element.tagName.toLowerCase()
      paths.unshift(index ? `${tagName}[${index + 1}]` : tagName)
      element = element.parentNode
    }

    return paths.length ? `/${paths.join("/")}` : ""
  }
  //  获取点击区域
  getClickArea(target) {
    // 遍历所有预定义区域选择器
    for (const area of this.areaSelectors) {
      if (this.isElementInArea(target, area.selector)) {
        return area.name
      }
    }
    return "其他" // 默认返回"其他"
  }
  // 判断元素是否在指定区域内
  isElementInArea(element, selector) {
    let currentElement = element
    // 向上遍历DOM树直到body元素
    while (currentElement && currentElement !== document.body) {
      if (currentElement.matches(selector)) {
        return true
      }
      currentElement = currentElement.parentElement
    }
    return false
  }

  // 修改 checkStorage 方法
  checkStorage() {
    const limitRatio = 0.9
    const maxLimit = 5 * 1024 * 1024 // 5MB
    let currentUsage = this.getLocalStorageUsage()

    // 转换字节为MB（保留2位小数）
    const usageMB = (currentUsage / 1024 / 1024).toFixed(2)
    const maxLimitMB = (maxLimit * limitRatio) / 1024 / 1024

    if (usageMB >= 5 * limitRatio) {
      console.log(`当前存储使用量：${usageMB} MB / ${maxLimitMB} MB`)
    }

    if (currentUsage >= maxLimit * limitRatio) {
      console.warn("存储空间即将用尽，清理旧数据")
      this.cleanupOldBatches() // 清理过期批次

      // 再次检查，如果仍满则删除最早批次
      currentUsage = this.getLocalStorageUsage()
      while (currentUsage >= maxLimit * limitRatio) {
        const oldest = this.findOldestBatch()
        if (!oldest) break // 无批次可删

        console.log(`强制删除最早批次 ${oldest.number}`, oldest)
        this.deleteBatch(oldest.number)
        currentUsage = this.getLocalStorageUsage()
      }
    }
  }

  getLocalStorageUsage() {
    let total = 0
    for (let i = 1; i <= this.metadata.currentBatch; i++) {
      const batchKey = `${this.storageKey}_${i}`
      const data = localStorage.getItem(batchKey) || ""
      total += batchKey.length * 2 + data.length * 2
    }
    return total
  }

  cleanupOldBatches() {
    const meta = this.metadata
    Object.entries(meta.batches).forEach(([batchNumber, batch]) => {
      if (this.isBatchExpired(batch.created)) {
        // 使用创建时间判断
        localStorage.removeItem(`${this.storageKey}_${batchNumber}`)
        delete meta.batches[batchNumber]
      }
    })
    this.updateMetadata(meta)
  }

  // 新增方法：查找最早创建的批次
  findOldestBatch() {
    const meta = this.metadata
    let oldest = null
    Object.entries(meta.batches).forEach(([number, batch]) => {
      if (!oldest || batch.created < oldest.created) {
        oldest = { number, ...batch }
      }
    })
    return oldest
  }

  // 新增方法：删除指定批次
  deleteBatch(batchNumber) {
    const meta = this.metadata
    localStorage.removeItem(`${this.storageKey}_${batchNumber}`)
    delete meta.batches[batchNumber]
    this.updateMetadata(meta)
  }
}

export default new UserTrackingService()
